/**
 * The endorphin kicks — confetti, floating "+XP", sounds and a buzz.
 *
 * Everything here is decoration and must never throw or block: a phone with
 * no AudioContext, no vibration or reduced motion still gets the lesson.
 * Reduced motion turns the particles off (the words and colours still say
 * what happened).
 */

import { nativeHaptic } from './native'

const reduced = () => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches

/* ── Sound ─────────────────────────────────────────────────────────────── */

let ctx: AudioContext | null = null

export function soundOn(): boolean {
  try { return localStorage.getItem('sound') !== 'off' } catch { return true }
}

export function setSound(on: boolean): void {
  try { localStorage.setItem('sound', on ? 'on' : 'off') } catch { /* private mode */ }
}

function audio(): AudioContext | null {
  if (!soundOn()) return null
  try {
    // Safari mutes Web Audio when the ringer switch is on silent unless the
    // page says it is media playback. Safari 17+; elsewhere it is a no-op.
    const session = (navigator as { audioSession?: { type: string } }).audioSession
    if (session && session.type !== 'playback') session.type = 'playback'
    ctx ??= new AudioContext()
    if (ctx.state === 'suspended') void ctx.resume()
    return ctx
  } catch {
    return null
  }
}

function tone(freq: number, at: number, dur: number, type: OscillatorType = 'sine', vol = 0.12): void {
  const a = audio()
  if (!a) return
  const o = a.createOscillator()
  const g = a.createGain()
  o.type = type
  o.frequency.value = freq
  g.gain.setValueAtTime(0.0001, a.currentTime + at)
  g.gain.exponentialRampToValueAtTime(vol, a.currentTime + at + 0.015)
  g.gain.exponentialRampToValueAtTime(0.0001, a.currentTime + at + dur)
  o.connect(g).connect(a.destination)
  o.start(a.currentTime + at)
  o.stop(a.currentTime + at + dur + 0.05)
}

export type Kind = 'tap' | 'right' | 'close' | 'wrong' | 'idk' | 'poll' | 'level' | 'callon'

export function sound(kind: Kind): void {
  try {
    switch (kind) {
      case 'tap': tone(880, 0, 0.06, 'triangle', 0.08); break
      case 'right': [523, 659, 784, 1047].forEach((f, i) => tone(f, i * 0.07, 0.18, 'triangle')); break
      case 'close': [523, 659, 784].forEach((f, i) => tone(f, i * 0.08, 0.16, 'triangle')); break
      // Soft and rising — "almost", never a buzzer.
      case 'wrong': tone(392, 0, 0.16, 'sine', 0.09); tone(523, 0.12, 0.2, 'sine', 0.09); break
      case 'idk': tone(440, 0, 0.12, 'sine', 0.08); tone(587, 0.1, 0.18, 'sine', 0.08); break
      case 'poll': tone(698, 0, 0.1, 'triangle', 0.09); tone(880, 0.08, 0.12, 'triangle', 0.09); break
      case 'level': [523, 659, 784, 1047, 1319].forEach((f, i) => tone(f, i * 0.09, 0.3, 'square', 0.06)); break
      case 'callon': [784, 988, 784, 988].forEach((f, i) => tone(f, i * 0.12, 0.12, 'square', 0.06)); break
    }
  } catch { /* decoration */ }
}

export function buzz(pattern: number | number[]): void {
  try { navigator.vibrate?.(pattern) } catch { /* not supported */ }
}

/* ── Confetti ──────────────────────────────────────────────────────────── */

const COLORS = ['#7c3aed', '#f59e0b', '#16a34a', '#0ea5e9', '#ec4899', '#facc15', '#ef4444']

interface P { x: number; y: number; vx: number; vy: number; r: number; w: number; h: number; c: string; life: number; spin: number }

let canvas: HTMLCanvasElement | null = null
let parts: P[] = []
let running = false

function ensureCanvas(): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null
  if (!canvas) {
    canvas = document.createElement('canvas')
    canvas.className = 'fx-canvas'
    canvas.setAttribute('aria-hidden', 'true')
    document.body.appendChild(canvas)
  }
  const dpr = window.devicePixelRatio || 1
  if (canvas.width !== innerWidth * dpr) {
    canvas.width = innerWidth * dpr
    canvas.height = innerHeight * dpr
  }
  const c = canvas.getContext('2d')
  c?.setTransform(dpr, 0, 0, dpr, 0, 0)
  return c
}

function loop(): void {
  const c = ensureCanvas()
  if (!c) { running = false; return }
  c.clearRect(0, 0, innerWidth, innerHeight)
  parts = parts.filter((p) => p.life > 0 && p.y < innerHeight + 40)
  for (const p of parts) {
    p.vy += 0.25
    p.vx *= 0.99
    p.x += p.vx
    p.y += p.vy
    p.r += p.spin
    p.life -= 1
    c.save()
    c.globalAlpha = Math.min(1, p.life / 30)
    c.translate(p.x, p.y)
    c.rotate(p.r)
    c.fillStyle = p.c
    c.fillRect(-p.w / 2, -p.h / 2, p.w, p.h)
    c.restore()
  }
  if (parts.length) requestAnimationFrame(loop)
  else running = false
}

/** A burst of confetti from a point (defaults to screen centre). */
export function confetti(count = 60, x?: number, y?: number, spread = 1): void {
  if (reduced()) return
  try {
    const ox = x ?? innerWidth / 2
    const oy = y ?? innerHeight / 2.5
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2
      const v = (3 + Math.random() * 7) * spread
      parts.push({
        x: ox, y: oy, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 5,
        r: Math.random() * 6, w: 6 + Math.random() * 6, h: 4 + Math.random() * 6,
        c: COLORS[i % COLORS.length] as string, life: 70 + Math.random() * 50, spin: (Math.random() - 0.5) * 0.4,
      })
    }
    if (!running) { running = true; requestAnimationFrame(loop) }
  } catch { /* decoration */ }
}

/** Confetti from both bottom corners — for the big moments. */
export function celebrate(): void {
  if (reduced()) return
  confetti(70, 0, innerHeight, 1.4)
  confetti(70, innerWidth, innerHeight, 1.4)
}

/** A floating label ("+120 XP", "¡Boom!") that rises and fades from an element or point. */
export function floatText(text: string, from?: { x: number; y: number }, tone = 'xp'): void {
  try {
    const el = document.createElement('div')
    el.className = `fx-float fx-${tone}`
    el.textContent = text
    el.setAttribute('aria-hidden', 'true')
    el.style.left = `${from?.x ?? innerWidth / 2}px`
    el.style.top = `${from?.y ?? innerHeight / 2}px`
    document.body.appendChild(el)
    setTimeout(() => el.remove(), 1400)
  } catch { /* decoration */ }
}

/** The kick on EVERY tap, before the server has answered. */
export function tapKick(e?: { clientX: number; clientY: number }, label?: string): void {
  sound('tap')
  buzz(15)
  const at = e ? { x: e.clientX, y: e.clientY } : undefined
  confetti(14, at?.x, at?.y, 0.6)
  if (label) floatText(label, at ? { x: at.x, y: at.y - 30 } : undefined, 'tap')
}

/** The kick when the answer comes back. */
export function resultKick(result: 'right' | 'close' | 'wrong' | 'idk' | 'poll', xp: number): void {
  sound(result)
  // In the iOS app the Taptic Engine answers; navigator.vibrate does not exist there.
  if (result === 'right') { if (!nativeHaptic('success')) buzz([30, 40, 30]); celebrate() }
  else if (result === 'close') { if (!nativeHaptic('success')) buzz([30, 40]); confetti(50) }
  else if (result === 'poll') { confetti(30) }
  // Wrong is gentle on purpose — sun-yellow, never red — so a light tap, not a warning.
  else if (!nativeHaptic('light')) buzz(20)
  if (xp > 0) floatText(`+${xp} XP`, { x: innerWidth / 2, y: innerHeight / 3 })
}
